import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { InfoPopover } from '../../components/InfoPopover';
import { MapWidget, type MapNameResolvers } from '../map/MapWidget';
import { useAsOfTimeline } from '../map/useAsOfTimeline';
import { waterByAllocations, workStatusByContour } from '../map/layers';
import { api } from '../../api/client';
import { useRequest } from '../../api/useRequest';
import { Skeleton, SkeletonRegion } from '../ui/Skeleton';
import { isLoading } from '../ui/AsyncBlock';
import { useAnalysisContext } from '../../analysis/context';
import { paramsKey } from '../data-source/api/adapter';
import { cycleChoices, irrigationWorks, pickCycle, pickWork, workKey } from '../data-source/api/water';
import type { ColorBy, WorkStatusValue } from '../map/paint';
import { usePageTrail } from '../shell/ShellContext';
import { useTrackData } from '../data/useTrackData';
import { useContourGeo } from '../map/useContourGeo';
import { FILES } from '../data/loader';
import { num, windowLabel } from '../lib/format';
import type {
  ContourMeta,
  Crop,
  Cycle,
  Farm,
  Massif,
  Obligation,
  QuarantineFile,
  QuarantineStatusValue,
  Work,
} from '../data/types';
import './pages.css';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  levelDistrict: {
    id: 'map.station.trail.district',
    defaultMessage: 'Район',
    description: 'Страница «Карта»: уровень первой ступени хлебных крошек',
  },
  districtName: {
    id: 'map.station.trail.districtName',
    defaultMessage: 'Бука',
    description: 'Страница «Карта»: название района (Бука) в хлебных крошках; имя собственное',
  },
  levelSection: {
    id: 'map.station.trail.section',
    defaultMessage: 'Раздел',
    description: 'Страница «Карта»: уровень второй ступени хлебных крошек',
  },
  trailName: {
    id: 'map.station.trail.name',
    defaultMessage: 'Карта',
    description: 'Страница «Карта»: название раздела в хлебных крошках',
  },
  title: {
    id: 'map.station.title',
    defaultMessage: 'Карта',
    description: 'Страница «Карта»: заголовок страницы',
  },
  purpose: {
    id: 'map.station.purpose',
    defaultMessage: 'Один слой за раз. Клик по контуру открывает его карточку.',
    description: 'Страница «Карта»: подзаголовок страницы под заголовком',
  },
  helpLabel: {
    id: 'map.station.help.label',
    defaultMessage: 'Как читать карту',
    description: 'Страница «Карта»: aria-label значка «i» в шапке',
  },
  helpHeading: {
    id: 'map.station.help.heading',
    defaultMessage: 'Как читать карту',
    description: 'Страница «Карта»: заголовок окна пояснения по значку «i»',
  },
  helpCrop: {
    id: 'map.station.help.crop',
    defaultMessage: 'Культуры — то, что стоит на поле на дату среза по демо-слою базы района (демонстрационные данные), а не подтверждённый посев. После уборки поле занимает следующая культура, поэтому осенью хлопка на карте нет: сдвиньте ползунок даты на сезон.',
    description: 'Страница «Карта»: абзац пояснения: слой «Культуры 2026»',
  },
  helpWork: {
    id: 'map.station.help.work',
    defaultMessage: 'Статус работы — состояние работ на полях по выбранной работе: отчитались, окно идёт, впереди, без отчёта. «Без отчёта» — не «не сделано» и никогда не красный.',
    description: 'Страница «Карта»: абзац пояснения: слой «Статус работы»; четыре состояния совпадают с подписями легенды',
  },
  helpWater: {
    id: 'map.station.help.water',
    defaultMessage: 'Вода — подача по одному поливу: подано — не меньше 95 % запрошенного, частично — меньше, не подано — ноль при запросе; объём не измерен — «не измерено», а не ноль.',
    description: 'Страница «Карта»: абзац пояснения: слой «Вода»',
  },
  helpQuarantine: {
    id: 'map.station.help.quarantine',
    defaultMessage: 'Карантин — статус по ленте событий; контур без событий зелёный по умолчанию: «событий не было», а не «проверено». Красный назначает только человек.',
    description: 'Страница «Карта»: абзац пояснения: слой «Карантин»',
  },
  helpGaps: {
    id: 'map.station.help.gaps',
    defaultMessage: 'Контур без заливки в выборку слоя не входит. Ползунок даты меняет дату состояния всех экранов.',
    description: 'Страница «Карта»: абзац пояснения: контуры без заливки и ползунок даты',
  },
  pickLoading: {
    id: 'map.station.pick.loading',
    defaultMessage: 'работы для раскраски',
    description: 'Страница «Карта»: что грузится (в винительном падеже), подпись скелетона для чтения с экрана',
  },
  pickWork: {
    id: 'map.station.pick.work',
    defaultMessage: 'Раскрасить по работе:',
    description: 'Страница «Карта»: подпись над кнопками выбора работы для слоя «Статус работы»',
  },
  pickOpen: {
    id: 'map.station.pick.open',
    defaultMessage: '{name} · идёт',
    description: 'Страница «Карта»: кнопка выбора работы: окно работы идёт; {name} — название работы',
  },
  pickClosed: {
    id: 'map.station.pick.closed',
    defaultMessage: '{name} · закрыто',
    description: 'Страница «Карта»: кнопка выбора работы: окно работы закрыто; {name} — название работы',
  },
  pickCycle: {
    id: 'map.station.pick.cycle',
    defaultMessage: 'агроцикл {name}',
    description: 'Страница «Карта»: подпись справа от кнопок выбора работы; {name} — название агроцикла («Хлопок-2026»)',
  },
  pickCycleLink: {
    id: 'map.station.pick.cycleLink',
    defaultMessage: 'агроцикл <link>{name}</link>',
    description: 'Страница «Карта»: то же со ссылкой на страницу агроцикла; {name} — название агроцикла, <link> — ссылка',
  },
  pickIrrigation: {
    id: 'map.station.pick.irrigation',
    defaultMessage: 'Полив:',
    description: 'Страница «Карта»: подпись над кнопками выбора полива для слоя «Вода»',
  },
  pickIrrigationRunning: {
    id: 'map.station.pick.irrigationRunning',
    defaultMessage: '{name} · идёт',
    description: 'Страница «Карта»: кнопка выбора полива: полив идёт; {name} — название полива',
  },
  rCanonFailed: {
    id: 'map.station.reason.canonFailed',
    defaultMessage: 'статус работ по контурам не прочитан ({error}) — раскрашивать нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; {error} — текст ошибки',
  },
  rCanonLoading: {
    id: 'map.station.reason.canonLoading',
    defaultMessage: 'работы агроцикла и статус по контурам ещё читаются — раскраска появится сама',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; данные ещё грузятся',
  },
  rNoCycles: {
    id: 'map.station.reason.noCycles',
    defaultMessage: 'агроциклов в срезе нет — раскрашивать по работе нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; в срезе нет агроциклов',
  },
  rNoWorkOnDate: {
    id: 'map.station.reason.noWorkOnDate',
    defaultMessage: 'у агроцикла «{name}» нет ни идущей, ни закрывшейся работы на дату — раскрашивать по работе нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; {name} — название агроцикла',
  },
  rNoRows: {
    id: 'map.station.reason.noRows',
    defaultMessage: 'по работе «{name}» работ на полях нет',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; {name} — название работы',
  },
  rWaterNoApi: {
    id: 'map.station.reason.waterNoApi',
    defaultMessage: 'подача воды по контурам есть только в базе района — в демонстрационном наборе раскрашивать нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; слой «Вода» недоступен на демонстрационных данных',
  },
  rWaterFailed: {
    id: 'map.station.reason.waterFailed',
    defaultMessage: 'подачи полива не прочитаны ({error}) — раскрашивать нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; {error} — текст ошибки',
  },
  rWaterLoading: {
    id: 'map.station.reason.waterLoading',
    defaultMessage: 'поливы и подачи ещё читаются — раскраска появится сама',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; данные ещё грузятся',
  },
  rWaterNone: {
    id: 'map.station.reason.waterNone',
    defaultMessage: 'в идущем агроцикле нет поливов — раскрашивать по воде нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; в агроцикле нет ни одного полива',
  },
  rWaterEmpty: {
    id: 'map.station.reason.waterEmpty',
    defaultMessage: 'по поливу «{name}» распределений по контурам нет',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; {name} — название полива',
  },
  rFxCyclesLoading: {
    id: 'map.station.reason.fxCyclesLoading',
    defaultMessage: 'список агроциклов ещё читается — раскраска появится сама',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; демонстрационные данные: список агроциклов грузится',
  },
  rFxNoCycles: {
    id: 'map.station.reason.fxNoCycles',
    defaultMessage: 'агроциклов в срезе нет ({reason}) — раскрашивать по работе нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; демонстрационные данные; {reason} — причина',
  },
  rFxCyclesNotRead: {
    id: 'map.station.reason.fxCyclesNotRead',
    defaultMessage: 'список агроциклов не прочитан',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; причина по умолчанию, когда список агроциклов не прочитан',
  },
  rFxNotAssembled: {
    id: 'map.station.reason.fxNotAssembled',
    defaultMessage: 'работы на полях агроцикла «{name}» по контурам в этом источнике не собраны ({reason}) — раскрашивать по работе нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; демонстрационные данные; {name} — название агроцикла, {reason} — причина',
  },
  rFxNotConnected: {
    id: 'map.station.reason.fxNotConnected',
    defaultMessage: 'набор не подключён',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; причина по умолчанию, когда источник не подключён',
  },
  rFxLoading: {
    id: 'map.station.reason.fxLoading',
    defaultMessage: 'работы агроцикла «{name}» ещё не прочитаны: данных много, раскраска появится сама',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; демонстрационные данные; {name} — название агроцикла',
  },
  rFxNoPickable: {
    id: 'map.station.reason.fxNoPickable',
    defaultMessage: 'у агроцикла «{name}» нет ни идущей, ни последней закрытой работы — раскрашивать по работе нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; демонстрационные данные; {name} — название агроцикла',
  },
  rFxNoRows: {
    id: 'map.station.reason.fxNoRows',
    defaultMessage: 'в данных агроцикла нет ни одной строки по работе «{name}»',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; демонстрационные данные; {name} — название работы',
  },
  rQuarantineMissing: {
    id: 'map.station.reason.quarantineMissing',
    defaultMessage: 'статусов карантина в этом источнике нет ({reason}) — раскрашивать по карантину нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; {reason} — причина',
  },
  rCategoryNone: {
    id: 'map.station.reason.categoryNone',
    defaultMessage: 'категория земель не названа ни у одного контура среза — раскрашивать по категории нечем',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; слой «Категории земель»',
  },
  rCropNone: {
    id: 'map.station.reason.cropNone',
    defaultMessage: 'культура 2026 не проставлена ни одному контуру среза — это пробел источника, а не «пар»',
    description: 'Страница «Карта»: пояснение под картой вместо раскраски (строчная фраза без точки), когда слой нечем питать; слой «Культуры 2026»',
  },
  capCrop: {
    id: 'map.station.caption.crop',
    defaultMessage: 'Культура на дату среза по демо-слою базы района, не подтверждённый посев.',
    description: 'Страница «Карта»: подпись слоя «Культуры 2026» над картой, когда хлопок на карте есть',
  },
  capNoCotton: {
    id: 'map.station.caption.noCotton',
    defaultMessage: 'Хлопка на эту дату на полях нет — сдвиньте дату на сезон (например, 15 авг).',
    description: 'Страница «Карта»: подпись слоя «Культуры 2026» над картой, когда на выбранную дату хлопка нет; «15 авг» — пример даты',
  },
  capWater: {
    id: 'map.station.caption.water',
    defaultMessage: 'Подача по поливу «{name}», {window}.',
    description: 'Страница «Карта»: подпись слоя «Вода» над картой; {name} — название полива, {window} — окно полива («1–20 июля»)',
  },
  capQuarantine: {
    id: 'map.station.caption.quarantine',
    defaultMessage: 'Без событий — зелёный по умолчанию, а не «проверено».',
    description: 'Страница «Карта»: подпись слоя «Карантин» над картой',
  },
  capCanonWork: {
    id: 'map.station.caption.canonWork',
    defaultMessage: 'Работа «{name}», {window}. Полей: отчитались {reported} · окно идёт {inWindow} · без отчёта после срока {overdue} · ждут окна {awaiting}.',
    description: 'Страница «Карта»: подпись слоя «Статус работы» над картой; {name} — работа, {window} — окно работы, остальное — числа полей (контуров) по состояниям',
  },
  capWork: {
    id: 'map.station.caption.work',
    defaultMessage: 'Работа «{name}», {window}.',
    description: 'Страница «Карта»: подпись слоя «Статус работы» над картой (демонстрационные данные); {name} — работа, {window} — окно работы',
  },
});


/** Слои станции: один за раз (план v2, §3). Подписи — `COLOR_BY_LABEL` кадра. */
const STATION_LAYERS: ColorBy[] = ['crop', 'workStatus', 'water', 'quarantine', 'landCategory', 'geometryStatus'];

/** «Хлопок-2026: Полив №2 (цветение)» → «Полив №2 (цветение)»: цикл назван рядом. */
const shortWorkName = (name: string) => name.replace(/^[^:]{1,40}:\s*/, '');

/** Коды категорий земли, которые знает легенда карты (`map/paint.ts`). */
const KNOWN_CATEGORIES: ReadonlySet<string> = new Set(['arable_irr', 'arable_rain', 'orchard', 'vineyard', 'pasture', 'other']);

export function MapStation() {
  const t = useT();
  usePageTrail([{ level: t(m.levelDistrict), name: t(m.districtName), to: '/' }, { level: t(m.levelSection), name: t(m.trailName) }]);

  const [colorBy, setColorBy] = useState<ColorBy>('crop');
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [waterKey, setWaterKey] = useState<string | null>(null);
  const { params } = useAnalysisContext();

  const crops = useTrackData<Crop[]>(FILES.crops);
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const farms = useTrackData<Farm[]>(FILES.farms);
  const quarantine = useTrackData<QuarantineFile>(FILES.quarantine);
  const cycles = useTrackData<Cycle[]>(FILES.cycles);
  const contours = useTrackData<ContourMeta[]>(FILES.contours);
  // Та же выдача, что у полотна (память общая): по ней видно, чем слой
  // культур действительно красится.
  const geo = useContourGeo();

  /**
   * Слой «Статус работы» питается обязательствами идущего цикла. Станция
   * не знает про цикл из адреса, поэтому берёт активный цикл среза.
   */
  const cycle = useMemo(() => {
    const list = cycles.data ?? [];
    return list.find((row) => row.status === 'active' && row.crop.toLowerCase() === 'cotton') ?? list.find((row) => row.status === 'active') ?? list[list.length - 1] ?? null;
  }, [cycles.data]);

  // Файлы работ и обязательств тянутся только когда слой действительно нужен:
  // obligations — несколько мегабайт, и грузить их ради культур незачем.
  // На каноне статус работы по контурам отдаёт сервер (`season/contour-status`),
  // файлы фикстур не нужны.
  const wantWork = colorBy === 'workStatus' && geo.origin !== 'api';
  const works = useTrackData<Work[]>(wantWork && cycle ? FILES.works(cycle.id) : null);
  const obligations = useTrackData<Obligation[]>(
    wantWork && cycle ? FILES.obligations(cycle.id) : null,
  );

  /* ------------------------------------------------------------- имена */

  const massifName = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of massifs.data ?? []) map.set(row.id, row.name);
    return map;
  }, [massifs.data]);

  const farmName = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of farms.data ?? []) map.set(row.id, row.name);
    return map;
  }, [farms.data]);

  const cropName = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of crops.data ?? []) map.set(row.id, row.name);
    return map;
  }, [crops.data]);

  /** Цикл называется культурой, а не её идентификатором из фикстуры. */
  const cycleLabel = useMemo(() => {
    if (!cycle) return null;
    return `${cropName.get(cycle.crop) ?? cycle.crop} ${cycle.season_label}`;
  }, [cycle, cropName]);

  const resolveNames = useMemo<MapNameResolvers>(
    () => ({
      farm: (id) => farmName.get(id) ?? null,
      massif: (id) => massifName.get(id) ?? null,
    }),
    [farmName, massifName],
  );

  /* -------------------------------------------------------- карантин */

  const quarantineById = useMemo(() => {
    const map: Record<string, QuarantineStatusValue> = {};
    for (const row of quarantine.data?.statuses ?? []) map[row.contour_id] = row.status;
    return map;
  }, [quarantine.data]);

  /* ------------------------------------------------------ выбор работы */

  const ordered = useMemo(
    () => [...(works.data ?? [])].sort((a, b) => a.seq - b.seq),
    [works.data],
  );

  /** Раскрашивать можно по идущим окнам и по последнему закрытому. */
  const pickable = useMemo(() => {
    const active = ordered.filter((w) => w.status === 'active');
    const lastDone = [...ordered].reverse().find((w) => w.status === 'done') ?? null;
    return lastDone ? [...active, lastDone] : active;
  }, [ordered]);

  const picked = pickable.find((w) => w.id === pickedId) ?? pickable[0] ?? null;

  const workStatusById = useMemo(() => {
    if (!picked || !obligations.data) return undefined;
    const map: Record<string, WorkStatusValue> = {};
    for (const row of obligations.data) {
      if (row.work_id !== picked.id) continue;
      map[row.contour_id] = row.state;
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [picked, obligations.data]);

  /* ------------------------------------------------------------ вода */

  /**
   * Слой «Вода»: распределения одного полива по контурам (`/water/works`).
   * Только на каноне и только когда слой выбран: цикл — идущий хлопковый,
   * полив — идущий, иначе последний закрытый (как на странице «Вода»).
   */
  const wantWater = colorBy === 'water' && geo.origin === 'api';
  /** Слой «Статус работы» на каноне: идущий цикл и его работы — из картины сезона. */
  const wantCanonWork = colorBy === 'workStatus' && geo.origin === 'api';
  const wantCanonCycle = wantWater || wantCanonWork;
  const pkey = paramsKey(params);
  const waterCycles = useRequest(
    () => (wantCanonCycle ? api.seasonCycles(params) : Promise.resolve(null)),
    [wantCanonCycle, pkey],
  );
  const waterCycle = useMemo(() => {
    const choices = waterCycles.data ? cycleChoices(waterCycles.data) : [];
    return (
      choices.find((c) => c.status === 'active' && c.cycle.toUpperCase().endsWith(':COTTON')) ??
      pickCycle(choices, null)
    );
  }, [waterCycles.data]);
  const waterPicture = useRequest(
    () =>
      wantCanonCycle && waterCycle
        ? api.seasonPicture({ ...params, cycle: waterCycle.cycle, summary: true, weeks: 0 })
        : Promise.resolve(null),
    [wantCanonCycle, pkey, waterCycle?.cycle],
  );
  const irrigation = useMemo(
    () => (waterPicture.data ? irrigationWorks(waterPicture.data) : []),
    [waterPicture.data],
  );
  const waterPicked = pickWork(irrigation, waterKey);
  const waterWork = useRequest(
    () =>
      wantWater && waterPicked
        ? api.waterWork(waterPicked.workDefId, { ...params, campaign_id: waterPicked.campaignId })
        : Promise.resolve(null),
    [wantWater, pkey, waterPicked?.workDefId, waterPicked?.campaignId],
  );
  const waterById = useMemo(
    () => {
      if (!wantWater || !waterWork.data) return undefined;
      // Вода полива — события файла ирригации (заявок и позиций нет): контур с водой — «подано».
      const bal = waterWork.data.balance;
      const fromFile = bal.requests === 0 && bal.lines === 0 && bal.deliveries > 0;
      return waterByAllocations(waterWork.data.allocations, fromFile);
    },
    [wantWater, waterWork.data],
  );

  const waterData = useMemo(() => (waterById ? { waterById } : undefined), [waterById]);

  /* ------------------------------------------ статус работы (канон) */

  /**
   * Работы, по которым можно красить: окна, идущие на дату состояния, и
   * последнее закрывшееся (как в наборе прототипа). Дата — горизонт картины.
   */
  const canonPickable = useMemo(() => {
    const pic = waterPicture.data;
    if (!wantCanonWork || !pic) return [];
    const asOf = pic.horizon;
    const works = [...(pic.summary?.works ?? [])].filter((w) => w.window_from && w.window_to);
    const open = works
      .filter((w) => w.window_from! <= asOf && w.window_to! >= asOf)
      .sort((a, b) => a.window_to!.localeCompare(b.window_to!));
    const closed = works.filter((w) => w.window_to! < asOf).sort((a, b) => b.window_to!.localeCompare(a.window_to!));
    const list = closed[0] ? [...open, closed[0]] : open;
    return list.map((w) => ({
      id: String(w.work_def_id),
      workDefId: w.work_def_id,
      name: shortWorkName(w.name),
      open: w.window_to! >= asOf,
      windowFrom: w.window_from,
      windowTo: w.window_to,
    }));
  }, [wantCanonWork, waterPicture.data]);
  const canonPicked = canonPickable.find((w) => w.id === pickedId) ?? canonPickable[0] ?? null;
  const contourStatus = useRequest(
    () =>
      wantCanonWork && waterCycle && canonPicked
        ? api.seasonContourStatus({ ...params, cycle: waterCycle.cycle, work_def_id: canonPicked.workDefId })
        : Promise.resolve(null),
    [wantCanonWork, pkey, waterCycle?.cycle, canonPicked?.workDefId],
  );
  const canonStatusById = useMemo(
    () => (wantCanonWork && contourStatus.data ? workStatusByContour(contourStatus.data.items) : undefined),
    [wantCanonWork, contourStatus.data],
  );
  const canonWorkReason = useMemo(() => {
    if (!wantCanonWork) return null;
    const failed = waterCycles.error ?? waterPicture.error ?? contourStatus.error;
    if (failed) return t(m.rCanonFailed, { error: failed.message });
    if (waterCycles.state === 'loading' || waterPicture.state === 'loading' || contourStatus.state === 'loading') {
      return t(m.rCanonLoading);
    }
    if (!waterCycle) return t(m.rNoCycles);
    if (!canonPicked) return t(m.rNoWorkOnDate, { name: waterCycle.name });
    if (canonStatusById && Object.keys(canonStatusById).length === 0) {
      return t(m.rNoRows, { name: canonPicked.name });
    }
    return null;
  }, [wantCanonWork, waterCycles, waterPicture, contourStatus, waterCycle, canonPicked, canonStatusById, t]);

  const waterLayerReason = useMemo(() => {
    if (colorBy !== 'water') return null;
    if (geo.origin !== 'api') return t(m.rWaterNoApi);
    const failed = waterCycles.error ?? waterPicture.error ?? waterWork.error;
    if (failed) return t(m.rWaterFailed, { error: failed.message });
    if (waterCycles.state === 'loading' || waterPicture.state === 'loading' || waterWork.state === 'loading') {
      return t(m.rWaterLoading);
    }
    if (!waterPicked) return t(m.rWaterNone);
    if (waterById && Object.keys(waterById).length === 0) {
      return t(m.rWaterEmpty, { name: shortWorkName(waterPicked.name) });
    }
    return null;
  }, [colorBy, geo.origin, waterCycles, waterPicture, waterWork, waterPicked, waterById, t]);

  /**
   * Слой выбран, а работы нет — говорим причину. Пустая легенда над пустым
   * районом читалась бы как «работ нет», а это неправда про данные.
   */
  const workLayerReason = useMemo(() => {
    if (colorBy !== 'workStatus') return null;
    if (geo.origin === 'api') return canonWorkReason;
    if (cycles.state === 'loading') return t(m.rFxCyclesLoading);
    if (!cycle) {
      return t(m.rFxNoCycles, { reason: cycles.reason ?? t(m.rFxCyclesNotRead) });
    }

    const name = cycleLabel ?? cycle.id;
    // Отказ источника — не «ещё читается»: иначе экран обещал бы раскраску,
    // которая не придёт.
    if (works.state === 'missing' || obligations.state === 'missing') {
      const reason = (obligations.state === 'missing' ? obligations.reason : works.reason) ?? t(m.rFxNotConnected);
      return t(m.rFxNotAssembled, { name, reason });
    }
    if (works.data === null || obligations.data === null) {
      return t(m.rFxLoading, { name });
    }
    if (pickable.length === 0) {
      return t(m.rFxNoPickable, { name });
    }
    if (!workStatusById) {
      return t(m.rFxNoRows, { name: picked?.name ?? '—' });
    }
    return null;
  }, [
    colorBy,
    cycles.state,
    cycles.reason,
    cycle,
    cycleLabel,
    works.data,
    works.state,
    works.reason,
    obligations.data,
    obligations.state,
    obligations.reason,
    pickable.length,
    workStatusById,
    picked,
    geo.origin,
    canonWorkReason,
    t,
  ]);

  /**
   * Слои, которым в срезе нечем питаться. Отсутствие ≠ ноль: карантин без
   * набора статусов не «зелёный по умолчанию», категория «неизвестно» у всех
   * контуров — не «прочее», а культура, не проставленная ни одному контуру, —
   * пробел источника, а не район под паром.
   */
  const staticLayerReason = useMemo(() => {
    const rows = contours.data ?? [];
    if (colorBy === 'quarantine' && quarantine.state === 'missing') {
      return t(m.rQuarantineMissing, { reason: quarantine.reason ?? t(m.rFxNotConnected) });
    }
    if (colorBy === 'landCategory' && rows.length > 0 && rows.every((row) => !KNOWN_CATEGORIES.has(row.category))) {
      return t(m.rCategoryNone);
    }
    // Культура на каноне едет с фичей карты, а не со строкой реестра: смотрим
    // на то, чем слой действительно красится.
    const placed =
      rows.some((row) => row.crop_2026) ||
      (geo.geo?.features.some((feature) => feature.properties?.crop_2026) ?? false);
    if (colorBy === 'crop' && geo.state === 'ready' && !placed) {
      return t(m.rCropNone);
    }
    return null;
  }, [colorBy, contours.data, quarantine.state, quarantine.reason, geo.state, geo.geo, t]);

  /** Хлопок на дату среза есть хотя бы на одном контуре — по тому, чем слой красится. */
  const cottonPlaced = useMemo(
    () =>
      (geo.geo?.features ?? []).some((f) => String(f.properties?.crop_2026 ?? '').toUpperCase() === 'COTTON') ||
      (contours.data ?? []).some((row) => (row.crop_2026 ?? '').toUpperCase() === 'COTTON'),
    [geo.geo, contours.data],
  );

  /**
   * Подпись слоя — одна короткая строка над картой. Объяснения слоёв — в «i» шапки.
   */
  const caption = useMemo(() => {
    if (colorBy === 'crop' && geo.origin === 'api') {
      return cottonPlaced ? t(m.capCrop) : t(m.capNoCotton);
    }
    if (colorBy === 'water' && waterPicked && waterById) {
      return t(m.capWater, { name: shortWorkName(waterPicked.name), window: windowLabel(waterPicked.windowFrom, waterPicked.windowTo) });
    }
    if (colorBy === 'quarantine' && geo.origin === 'api') {
      return t(m.capQuarantine);
    }
    if (colorBy !== 'workStatus') return undefined;
    if (geo.origin === 'api') {
      if (!canonPicked || !canonStatusById || !contourStatus.data) return undefined;
      const totals = contourStatus.data.totals;
      return t(m.capCanonWork, {
        name: canonPicked.name,
        window: windowLabel(canonPicked.windowFrom, canonPicked.windowTo),
        reported: num(totals.reported),
        inWindow: num(totals.in_window),
        overdue: num(totals.overdue),
        awaiting: num(totals.awaiting_window),
      });
    }
    if (!picked || !workStatusById) return undefined;
    return t(m.capWork, { name: picked.name, window: windowLabel(picked.window.from, picked.window.to) });
  }, [colorBy, picked, workStatusById, geo.origin, cottonPlaced, waterPicked, waterById, canonPicked, canonStatusById, contourStatus.data, t]);

  const timeline = useAsOfTimeline();

  return (
    <div className="mapstation-page">
      <PageHeader
        title={t(m.title)}
        purpose={t(m.purpose)}
        actions={
          <InfoPopover icon="info" label={t(m.helpLabel)} heading={t(m.helpHeading)}>
            <p>{t(m.helpCrop)}</p>
            <p>{t(m.helpWork)}</p>
            <p>{t(m.helpWater)}</p>
            <p>{t(m.helpQuarantine)}</p>
            <p>{t(m.helpGaps)}</p>
          </InfoPopover>
        }
      />

      <div className="mapstation-page__body">
        <div className="mapstation-page__map" data-guide="map">
          {wantCanonWork && isLoading(waterPicture) ? (
            // Полоса выбора работы стоит на месте, пока картина идёт: карта под ней не прыгает (6al.49).
            <SkeletonRegion label={t(m.pickLoading)} className="mapstation-pick">
              <span className="mapstation-pick__label">{t(m.pickWork)}</span>
              {[120, 140, 110].map((w, i) => <Skeleton key={i} variant="block" width={w} height={32} className="skel--control" />)}
            </SkeletonRegion>
          ) : null}
          {colorBy === 'workStatus' && geo.origin === 'api' && canonPickable.length > 0 ? (
            <div className="mapstation-pick" data-guide="layer-pick">
              <span className="mapstation-pick__label">{t(m.pickWork)}</span>
              {canonPickable.map((work) => (
                <button
                  key={work.id}
                  type="button"
                  aria-pressed={canonPicked?.id === work.id}
                  className={`mapstation-pick__btn focus-ring ${canonPicked?.id === work.id ? 'is-on' : ''}`}
                  onClick={() => setPickedId(work.id)}
                >
                  {work.open ? t(m.pickOpen, { name: work.name }) : t(m.pickClosed, { name: work.name })}
                </button>
              ))}
              {waterCycle ? <span className="mapstation-pick__label">{t(m.pickCycle, { name: waterCycle.name })}</span> : null}
            </div>
          ) : null}

          {colorBy === 'workStatus' && geo.origin !== 'api' && pickable.length > 0 && obligations.state !== 'missing' ? (
            <div className="mapstation-pick" data-guide="layer-pick">
              <span className="mapstation-pick__label">{t(m.pickWork)}</span>
              {pickable.map((work) => (
                <button
                  key={work.id}
                  type="button"
                  aria-pressed={picked?.id === work.id}
                  className={`mapstation-pick__btn focus-ring ${
                    picked?.id === work.id ? 'is-on' : ''
                  }`}
                  onClick={() => setPickedId(work.id)}
                >
                  {work.status === 'active' ? t(m.pickOpen, { name: work.name }) : t(m.pickClosed, { name: work.name })}
                </button>
              ))}
              {cycle ? (
                <span className="mapstation-pick__label">
                  {t(m.pickCycleLink, {
                    name: cycleLabel ?? cycle.id,
                    link: (chunks) => <Link to={`/cycles/${cycle.id}`}>{chunks}</Link>,
                  })}
                </span>
              ) : null}
            </div>
          ) : null}

          {colorBy === 'water' && irrigation.length > 0 ? (
            <div className="mapstation-pick" data-guide="layer-pick">
              <span className="mapstation-pick__label">{t(m.pickIrrigation)}</span>
              {irrigation.map((work) => (
                <button
                  key={workKey(work)}
                  type="button"
                  aria-pressed={waterPicked !== null && workKey(waterPicked) === workKey(work)}
                  className={`mapstation-pick__btn focus-ring ${
                    waterPicked !== null && workKey(waterPicked) === workKey(work) ? 'is-on' : ''
                  }`}
                  onClick={() => setWaterKey(workKey(work))}
                >
                  {work.status === 'active' ? t(m.pickIrrigationRunning, { name: shortWorkName(work.name) }) : shortWorkName(work.name)}
                </button>
              ))}
              {waterCycle ? <span className="mapstation-pick__label">{t(m.pickCycle, { name: waterCycle.name })}</span> : null}
            </div>
          ) : null}

          {/* Подпись слоя — строкой над картой: подпись внутри кадра при панели
              слоёв садится на свёрнутую легенду (кадр 6al.1). */}
          {caption ? <p className="t-caption" style={{ margin: 0, flex: '0 0 auto' }}>{caption}</p> : null}

          <MapWidget
            colorBy={colorBy}
            onColorByChange={setColorBy}
            layersPanel
            layers={STATION_LAYERS}
            legend="collapsed"
            mode="full"
            fill
            crops={crops.data ?? undefined}
            quarantineById={quarantineById}
            workStatusById={geo.origin === 'api' ? canonStatusById : workStatusById}
            data={waterData}
            layerUnavailable={workLayerReason ?? waterLayerReason ?? staticLayerReason}
            resolveNames={resolveNames}
            timeline={timeline}
          />
        </div>
      </div>
    </div>
  );
}
